import { imprimir as imprimirHoja } from "../imprimir.ts";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { DECLARACIONES, textoAutorizacionDatos } from "../../../dominio/src/index.ts";
import { Membrete } from "./Membrete.tsx";
import { useClinica } from "../clinica.ts";
import type { Paciente } from "../api.ts";

/**
 * Hojas en blanco para imprimir y firmar en papel (el paciente firma a mano; no se registra nada en Donna).
 * Mismo texto que se acepta en pantalla, con líneas para nombre, cédula, fecha y firma.
 */

/** Muestra una hoja solo mientras se imprime: la monta, abre el diálogo de impresión y la quita. */
export function useHojaParaImprimir(): [ReactNode, (hoja: ReactNode) => void] {
  const [hoja, setHoja] = useState<ReactNode | null>(null);
  useEffect(() => {
    if (!hoja) return;
    const t = setTimeout(() => { imprimirHoja(); setHoja(null); }, 120);
    return () => clearTimeout(t);
  }, [hoja]);
  return [hoja ? createPortal(hoja, document.body) : null, setHoja];
}

const Linea = ({ etiqueta, valor, ancho }: { etiqueta: string; valor?: string; ancho?: string }) => (
  <div className="ff-linea" style={ancho ? { flex: `0 0 ${ancho}` } : undefined}>
    <span className="ff-valor">{valor ?? ""}</span><small>{etiqueta}</small>
  </div>
);

function Firmas({ otorga, nombre, doc, profesional }: { otorga: string; nombre: string; doc: string; profesional: boolean }) {
  return (
    <div className="ff-firmas">
      <div className="ff-fila"><Linea etiqueta="Lugar y fecha" ancho="46%" /><Linea etiqueta={`Nombre de quien ${otorga}`} valor={nombre} /></div>
      <div className="ff-fila"><Linea etiqueta="Cédula o pasaporte" valor={doc} ancho="46%" /><Linea etiqueta="Firma" /></div>
      {profesional && <div className="ff-fila"><Linea etiqueta="Profesional tratante (nombre y registro)" /><Linea etiqueta="Firma y sello" ancho="40%" /></div>}
    </div>
  );
}

/** El texto del consentimiento termina con las tres declaraciones; en papel van como casillas para marcar, no repetidas. */
const sinDeclaraciones = (texto: string) => texto.split("\n\n").filter((x) => x !== DECLARACIONES.join(" ")).join("\n\n");
const nombreDe = (p: Paciente) => `${p.nombres} ${p.apellidos}`.trim();
const datosPaciente = (p: Paciente) => `Paciente: ${nombreDe(p)}${p.doc ? ` · C.I. ${p.doc}` : ""} · HC ${p.hc}`;

/** Consentimiento informado de un procedimiento, listo para firmar a mano. */
export function ConsentimientoParaFirmar({ titulo, texto, paciente, otorgante }: {
  titulo: string; texto: string; paciente: Paciente; otorgante: { nombre: string; doc: string; rol: string };
}) {
  const representante = otorgante.rol === "representante";
  return (
    <div className="solo-impresion ff-hoja">
      <Membrete />
      <p>{datosPaciente(paciente)}</p>
      <div className="ff-texto">{sinDeclaraciones(texto)}</div>
      <p className="ff-sub">Declaraciones (marque cada una):</p>
      <ul className="ff-casillas">{DECLARACIONES.map((d) => <li key={d}><span className="ff-caja" aria-hidden="true">☐</span> {d}</li>)}</ul>
      <p>{representante ? "Firma el representante legal del paciente." : "Firma el paciente."} Si no acepta, escriba el motivo: ______________________________________________</p>
      <Firmas otorga={representante ? "otorga el consentimiento (representante legal)" : "otorga el consentimiento"} nombre={otorgante.nombre} doc={otorgante.doc} profesional />
      <p className="ff-pie">{titulo} · Documento generado con Donna para firma física · Conserve una copia firmada en la historia clínica.</p>
    </div>
  );
}

/** Autorización de datos personales y de mensajes (recordatorios, promociones y fotos), con casillas Sí/No por cada fin. */
export function AutorizacionDatosParaFirmar({ paciente, menor, representante }: { paciente: Paciente; menor: boolean; representante: { nombre: string; doc: string } | null }) {
  const c = useClinica();
  const t = textoAutorizacionDatos({ nombre: c?.nombre ?? "La clínica", razonSocial: c?.razonSocial, ruc: c?.ruc, correo: c?.correo, telefono: c?.telefono }, menor);
  const otorga = menor ? "firma como representante legal" : "firma como titular";
  return (
    <div className="solo-impresion ff-hoja">
      <Membrete />
      <h2 className="ff-titulo">{t.titulo}</h2>
      <p>{datosPaciente(paciente)}</p>
      {t.parrafos.map((x, i) => <p key={i}>{x}</p>)}
      <table className="ff-tabla">
        <thead><tr><th>Finalidad</th><th style={{ width: 150 }}>Mi decisión</th></tr></thead>
        <tbody>
          {t.fines.map((f) => (
            <tr key={f.clave}>
              <td><b>{f.texto}</b><br /><small>{f.detalle}</small></td>
              <td>{f.obligatorio ? <small>Necesario para la atención</small> : <><span className="ff-caja" aria-hidden="true">☐</span> Sí autorizo<br /><span className="ff-caja" aria-hidden="true">☐</span> No autorizo</>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="ff-sub">Mis derechos</p>
      {t.derechos.map((x, i) => <p key={i}>{x}</p>)}
      {t.cierre.map((x, i) => <p key={i}>{x}</p>)}
      <Firmas otorga={otorga} nombre={menor ? representante?.nombre ?? "" : nombreDe(paciente)} doc={menor ? representante?.doc ?? "" : paciente.doc} profesional={false} />
      {menor && <p className="ff-sub">Representante legal: {representante?.nombre || "______________________________"}</p>}
      <p className="ff-pie">Versión {t.version} · Documento generado con Donna para firma física · Conserve una copia firmada en la historia clínica.</p>
    </div>
  );
}
